import { createContext, useCallback, useContext, useEffect, useState, PropsWithChildren, useRef } from 'react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';
import useUserProfile from '@/api/userProfiles/useUserProfile.ts';
import type { Tables } from '$/database.types';

type UserProfile = Tables<'users'>;

interface AuthContextProps {
  session: Session | null;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  userProfile: UserProfile | null | undefined;
  isLoadingProfile: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  const logout = useCallback(() => {
    void supabaseClient.auth.signOut();
  }, []);

  const loginQuery = useLogin();
  const sendResetPasswordLinkQuery = useSendResetPasswordLink();
  const resetPasswordQuery = useResetPassword();
  const verifyEmailQuery = useVerifyEmail();
  const resendVerificationCodeQuery = useResendVerificationCode();

  const { data: userProfile, isLoading: isLoadingProfile } = useUserProfile(session?.user?.id);

  const checkData = useCallback(async (session: Session | null) => {
    if (isPendingAuthChangeCheck.current) return;

    isPendingAuthChangeCheck.current = true;

    if (!session) {
      setIsLoading(false);
      setSession(null);
      isPendingAuthChangeCheck.current = false;
      return;
    }

    setIsLoading(false);
    setSession(session);
    isPendingAuthChangeCheck.current = false;
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setSession(null);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        supabaseClient,
        logout,
        isLoading,
        userProfile,
        isLoadingProfile,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
